import React, { useState } from "react";
import { LessonI, FeedbackValue } from "../types";
import { CreateFeedbackRequest } from "../api/types";
import RatingScaleComponent from "../pages/CreateLessonPage/components/contentComponents/RatingScaleComponent";
import RadioGroup, { RadioGroupOption } from "./RadioGroup";
import Input from "./Input";
import Checkbox from "./Checkbox";

interface FeedbackProps {
  lesson: LessonI;
  moduleId?: string;
  onFeedbackChange?: (feedback: CreateFeedbackRequest | null) => void;
}

const FEEDBACK_OPTIONS: RadioGroupOption[] = [
  { label: "Not that useful", value: "not_at_all_useful" },
  { label: "Useful", value: "moderately_useful" },
  { label: "Very useful", value: "very_useful" },
];

const SUBFEEDBACK_OPTIONS: string[] = [
  "I learned new information",
  "The information was what I was looking for",
  "The content was clear",
  "I can see using these skills in my everyday life",
];

export function Feedback({
  lesson,
  moduleId,
  onFeedbackChange,
}: FeedbackProps) {
  const [selectedValue, setSelectedValue] = useState<FeedbackValue | null>(
    null,
  );
  const [subFeedbackRatings, setSubFeedbackRatings] = useState<
    Record<string, number | string>
  >({});
  const [otherFeedback, setOtherFeedback] = useState<string>("");
  const [canContact, setCanContact] = useState(false);

  const createFeedbackObject = (
    feedback: FeedbackValue,
    ratings?: Record<string, number | string>,
    otherFeedback?: string,
    contactEnabled?: boolean,
  ): CreateFeedbackRequest => ({
    lessonId: lesson._id,
    moduleId: moduleId,
    feedback,
    subFeedback: ratings
      ? Object.entries(ratings)
          .filter(([, value]) => value != null)
          .map(([label, value]) => `${label}: ${value}`)
      : [],
    otherFeedback: otherFeedback,
    createdAt: new Date(),
    canContact: contactEnabled,
  });

  const handlePrimaryFeedback = (option: RadioGroupOption | null) => {
    if (!option) {
      setSelectedValue(null);
      setSubFeedbackRatings({});
      onFeedbackChange?.(null);
      return;
    }

    const feedbackValue = option.value as FeedbackValue;
    setSelectedValue(feedbackValue);
    setSubFeedbackRatings({});
    onFeedbackChange?.(
      createFeedbackObject(feedbackValue, {}, otherFeedback, canContact),
    );
  };

  const handleRatingChange = (optionLabel: string, value: number | string) => {
    if (!selectedValue) return;

    const updatedRatings = {
      ...subFeedbackRatings,
      [optionLabel]: value,
    };
    setSubFeedbackRatings(updatedRatings);
    onFeedbackChange?.(
      createFeedbackObject(
        selectedValue,
        updatedRatings,
        otherFeedback,
        canContact,
      ),
    );
  };

  const renderDetailedOptions = () => {
    if (!selectedValue) return null;
    const options = SUBFEEDBACK_OPTIONS;

    return (
      <div className="space-y-4 border border-gray-200 rounded-lg p-4">
        {options.map((option) => (
          <div key={option}>
            <RatingScaleComponent
              value={subFeedbackRatings[option] ?? ""}
              onChange={(val) => handleRatingChange(option, val)}
              content={{
                label: option,
                minimumValue: 1,
                maximumValue: 5,
                numberOfSteps: 5,
                minimumLabel: "Disagree",
                maximumLabel: "Agree",
              }}
            />
          </div>
        ))}
        <Input
          label="Any additional feedback"
          value={otherFeedback}
          onChange={(e) => {
            setOtherFeedback(e.target.value);
            if (selectedValue) {
              onFeedbackChange?.(
                createFeedbackObject(
                  selectedValue,
                  subFeedbackRatings,
                  e.target.value,
                  canContact,
                ),
              );
            }
          }}
        />
        <div className="mt-4">
          <Checkbox
            label="I would like to be contacted about this feedback."
            checked={canContact}
            onChange={(e) => {
              setCanContact(e.target.checked);
              if (selectedValue) {
                onFeedbackChange?.(
                  createFeedbackObject(
                    selectedValue,
                    subFeedbackRatings,
                    otherFeedback,
                    e.target.checked,
                  ),
                );
              }
            }}
          />
        </div>
      </div>
    );
  };

  return (
    <div className={`space-y-4 p-4 ${selectedValue ? "pb-32" : ""}`}>
      <h3 className="font-medium mb-4">How useful was this section?</h3>
      <RadioGroup
        options={FEEDBACK_OPTIONS}
        selectedValue={selectedValue}
        direction="row"
        onChange={handlePrimaryFeedback}
      />
      <div className="mt-6">{renderDetailedOptions()}</div>
    </div>
  );
}
